import React from 'react';
import { createPortal } from 'react-dom';
import { AUTH_LOGOUT_REASONS } from './authLogoutReasons';
import { Button, ButtonLink } from './buttons';
import useDialogFocusManagement from './hooks/useDialogFocusManagement';
import { useText } from './i18n/I18nProvider';

const errorTargetRules = [
  { pattern: /mock exams? need a title|exam title|title/i, selector: 'input[name="title"]' },
  { pattern: /at least two answers|exactly one correct answer|answers?/i, selector: '.admin-mock-exam-answer-panel.is-invalid' },
  { pattern: /curricula need a name|curriculum name|roles need a name|topics need a name|units need a name|name/i, selector: '[name="name"]' },
  { pattern: /choose a curriculum|curriculum/i, selector: '[name="curriculum_id"]' },
  { pattern: /choose a topic|topic/i, selector: '[name="topic_id"]' },
  { pattern: /choose a saved unit|choose a unit|unit/i, selector: '[name="module_id"]' },
  { pattern: /username/i, selector: '[name="username"]' },
  { pattern: /email/i, selector: '[name="email"]' },
  { pattern: /school|organization/i, selector: '[name="organization"]' },
  { pattern: /password/i, selector: '[name="password"], [name="password_confirm"]' },
  { pattern: /image|media|url/i, selector: '.media-editor input[type="url"], .media-editor input[type="file"]' },
  { pattern: /failed to load resource|err_name_not_resolved|name_not_resolved|not resolved|load resource/i, selector: '.media-editor input[type="url"], .media-editor input[type="file"]' },
];

const fallbackErrorTargetSelectors = [
  '[aria-invalid="true"]',
  '.field.is-invalid input',
  '.field.is-invalid select',
  '.field.is-invalid textarea',
  '[data-exam-error-key].is-invalid',
  '.field-error',
  '.is-invalid',
];

function findErrorTarget(error) {
  if (typeof document === 'undefined') {
    return null;
  }

  const message = String(error || '');
  for (const rule of errorTargetRules) {
    if (!rule.pattern.test(message)) {
      continue;
    }
    const mappedTarget = document.querySelector(rule.selector);
    if (mappedTarget) {
      return mappedTarget;
    }
  }

  for (const selector of fallbackErrorTargetSelectors) {
    const target = document.querySelector(selector);
    if (target) {
      return target;
    }
  }
  return null;
}

function focusErrorTarget(error) {
  const target = findErrorTarget(error);
  if (!target) {
    return;
  }

  const section = target.closest?.('.media-editor-item, .media-editor') ||
    target.closest?.('[data-exam-error-key], .field, .admin-mock-exam-answer-panel, .admin-mock-exam-question-card, .is-invalid') ||
    target;
  section.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  section.classList?.add('error-target-highlight');
  target.classList?.add('error-target-highlight');
  window.setTimeout?.(() => {
    section.classList?.remove('error-target-highlight');
    target.classList?.remove('error-target-highlight');
  }, 2800);

  const focusTarget = section.querySelector?.('input, select, textarea, button, [tabindex]:not([tabindex="-1"])') || section;
  if (!focusTarget.hasAttribute?.('tabindex') && !['INPUT', 'SELECT', 'TEXTAREA', 'BUTTON', 'A'].includes(focusTarget.tagName)) {
    focusTarget.setAttribute?.('tabindex', '-1');
  }
  try {
    focusTarget.focus?.({ preventScroll: true });
  } catch {
    focusTarget.focus?.();
  }
  if (document.activeElement !== focusTarget) {
    focusTarget.focus?.();
  }
}

function canFocusErrorTarget(error) {
	const message = String(error || '');
	return errorTargetRules.some(rule => rule.pattern.test(message)) || Boolean(findErrorTarget(error));
}

export function SectionHeader({ eyebrow, title, description, badge }) {
  return (
    <div className="section-heading">
      <div>
        {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
        <h2>{title}</h2>
        <p className="section-copy">{description}</p>
      </div>
      {badge ? <span className="chip">{badge}</span> : null}
    </div>
  );
}

export function Message({ error, success }) {
  if (error) {
    if (!canFocusErrorTarget(error)) {
      return <div className="message error" role="alert">{error}</div>;
    }
    return (
      <Button variant="unstyled" className="message error message-action" role="alert" type="button" onClick={() => focusErrorTarget(error)}>
        {error}
      </Button>
    );
  }

  if (success) {
	return <div className="message success" role="status">{success}</div>;
  }

  return null;
}

export function LoadingSpinner({ className = '', size = 'default', label = 'Loading' }) {
  const classNames = ['loading-spinner', `loading-spinner-${size}`, className]
    .filter(Boolean)
    .join(' ');

  return (
    <span
      aria-label={label}
      className={classNames}
      role="status"
    />
  );
}

export function LoadingPanel({ className = '', label }) {
  const t = useText();
  const resolvedLabel = label || t('common.loading.default');
  const classNames = ['loading-panel', className].filter(Boolean).join(' ');
  return (
    <div aria-busy="true" className={classNames}>
      <LoadingSpinner label={resolvedLabel} />
    </div>
  );
}

export function WorkspaceAccessDeniedPanel({ actionLabel = '', actionUrl = '', message, returnToLogin }) {
  const t = useText();

  function handleReturnToLogin() {
    returnToLogin(AUTH_LOGOUT_REASONS.workspaceAccessDenied);
  }

  return (
    <main className="layout">
      <section className="card loading-card">
        <p>{message}</p>
        <div className="form-actions">
          {actionUrl ? (
            <ButtonLink href={actionUrl} variant="primary">
              {actionLabel || t('common.actions.openSignIn')}
            </ButtonLink>
          ) : null}
          <Button variant="primary" type="button" onClick={handleReturnToLogin}>
            {t('common.actions.returnToSignIn')}
          </Button>
        </div>
      </section>
    </main>
  );
}

export function ConfirmActionPanel({
  busy,
  busyLabel,
  cancelLabel,
  confirmLabel,
  confirmTone = 'danger',
  description,
  eyebrow,
  error,
  hint,
  onCancel,
  onConfirm,
  title,
  warning,
}) {
  const t = useText();
  const resolvedCancelLabel = cancelLabel || t('common.actions.cancel');
  const resolvedConfirmLabel = confirmLabel || t('common.actions.delete');
  const confirmVariant = confirmTone === 'danger' ? 'confirmDanger' : 'confirmPrimary';
  const hasConfirmAction = typeof onConfirm === 'function';
  const id = React.useId();
  const titleID = `confirm-action-title-${id}`;
  const descriptionID = `confirm-action-description-${id}`;
  const hintID = hint ? `confirm-action-hint-${id}` : '';
  const { dialogRef, handleBackdropClick, initialFocusRef } = useDialogFocusManagement({
    busy,
    onClose: onCancel,
  });

  return createPortal((
    <div className="confirm-action-overlay" role="presentation" onClick={handleBackdropClick}>
      <section
        aria-busy={busy ? 'true' : undefined}
        aria-describedby={[descriptionID, hintID].filter(Boolean).join(' ')}
        aria-labelledby={titleID}
        aria-modal="true"
        className="confirm-action-panel card"
        ref={dialogRef}
        role={hasConfirmAction ? 'dialog' : 'alertdialog'}
        tabIndex="-1"
      >
        <p className={warning ? 'eyebrow confirm-action-warning-eyebrow' : 'eyebrow'}>{eyebrow || t('common.confirm.eyebrow')}</p>
        <h2 id={titleID}>{title}</h2>
        <p className="confirm-action-copy" id={descriptionID}>{description}</p>
        {error ? <Message error={error} /> : null}
        {warning ? (
          <div className="confirm-action-warning" role="alert">
            <span className="material-symbols-outlined" aria-hidden="true">warning</span>
            <span>{warning}</span>
          </div>
        ) : null}
        {hint ? <p className="confirm-action-hint" id={hintID}>{hint}</p> : null}
        <div className="confirm-action-actions">
          <Button ref={initialFocusRef} variant="confirmDismiss" type="button" onClick={onCancel} disabled={busy}>
            {resolvedCancelLabel}
          </Button>
          {hasConfirmAction ? (
            <Button variant={confirmVariant} type="button" onClick={onConfirm} disabled={busy}>
              {busy ? (busyLabel || `${resolvedConfirmLabel}...`) : resolvedConfirmLabel}
            </Button>
          ) : null}
        </div>
      </section>
    </div>
  ), document.body);
}
